import { useEffect, useState } from 'react';
import { confirmDialog } from '../../../shared/ConfirmModal';
import { formatWorkingSince, getInitials, useAppWorkingOn, useToggleWorkingOn } from './useAppWorkingOn';
import './workingOn.css';

// Class to add to any app card so it glows while someone is in the file.
export const WORKING_ON_CLASS = 'app-working-on';

function WorkingOnAvatar({ user, size = 18 }) {
    const [broken, setBroken] = useState(false);
    if (user?.image && !broken) {
        return <img className="wo-avatar" src={user.image} alt={user.name} style={{ width: size, height: size }} onError={() => setBroken(true)} />;
    }
    return <span className="wo-avatar wo-avatar-initials" style={{ width: size, height: size, fontSize: size * 0.45 }}>{getInitials(user?.name)}</span>;
}

// Re-render periodically so "5m ago" stays current.
function useNow(intervalMs = 30000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
}

export function WorkingOnToggle({ app, toast, compact = false }) {
    const { isWorking, saving, setWorking } = useToggleWorkingOn(app, toast);

    return (
        <button
            type="button"
            role="switch"
            aria-checked={isWorking}
            disabled={saving}
            className={`wo-switch ${isWorking ? 'is-on' : ''} ${compact ? 'is-compact' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                setWorking(!isWorking);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title={isWorking ? "Turn off \"I'm working on this file\"" : "I'm working on this file"}
        >
            <span className="wo-switch-track">
                <span className="wo-switch-thumb" />
            </span>
            <span className="wo-switch-label">I'm working on this file</span>
        </button>
    );
}

// Small badge for app cards.
export function WorkingOnChip({ app }) {
    const { workingBy, isMe } = useAppWorkingOn(app);
    if (!workingBy) return null;
    const firstName = workingBy.name?.trim().split(/\s+/)[0] || 'Someone';

    return (
        <span className="wo-chip" title={`${isMe ? 'You are' : `${workingBy.name} is`} in the file`}>
            <span className="wo-dot" />
            <WorkingOnAvatar user={workingBy} />
            <span className="wo-chip-text">{isMe ? "You're" : `${firstName} is`} in the file</span>
        </span>
    );
}

// Top-of-detail bar: banner while someone is in the file, plus the toggle.
export function WorkingOnBanner({ app, toast, inHeader = false }) {
    const { workingBy, workingSince, isMe, saving, setWorking } = useToggleWorkingOn(app, toast);
    useNow();

    if (app?.form_id == null) return null;

    const handleTakeOver = () => {
        confirmDialog({
            header: 'Take over?',
            message: `${workingBy?.name || 'Someone'} is in this file. Take over?`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Take over',
            rejectLabel: 'Cancel',
            accept: () => setWorking(true),
        });
    };

    return (
        <div className={`wo-banner ${workingBy ? 'is-active' : ''} ${inHeader ? 'wo-banner-header' : ''}`} onClick={(e) => e.stopPropagation()}>
            {workingBy ? (
                <div className="wo-banner-info">
                    <span className="wo-dot" />
                    <WorkingOnAvatar user={workingBy} size={26} />
                    <span>
                        <strong>{isMe ? 'You' : workingBy.name}</strong> {isMe ? 'are' : 'is'} in the file
                        {workingSince && <span className="wo-banner-since"> · since {formatWorkingSince(workingSince)}</span>}
                    </span>
                    {!isMe && (
                        <button type="button" className="wo-takeover" disabled={saving} onClick={handleTakeOver}>
                            Take over
                        </button>
                    )}
                </div>
            ) : (
                <div className="wo-banner-info wo-banner-idle">Nobody is in this file</div>
            )}
            <WorkingOnToggle app={app} toast={toast} />
        </div>
    );
}
